import {
  AutocompleteInputChangeReason,
  ListItem,
  ListItemText,
  SxProps,
} from "@mui/material";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Nullable } from "../../../../types/nullable";
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import { AutocompleteBaseSingle } from "../autocompleteBase/AutocompleteBaseSingle";
import {
  CustomerUserAutocompleteOption,
  formatCustomerUserOptionLabel,
} from "./customerUsersAutocompleteUtils";
import useCustomerUsersAutocomplete from "./useCustomerUsersAutocomplete";

type Props = {
  value: Nullable<CustomerUserAutocompleteOption>;
  onChange: (value: Nullable<CustomerUserAutocompleteOption>) => void;
  disabled?: boolean;
  placeholder?: string;
  sx?: SxProps;
};

const getOptionLabel = (option: CustomerUserAutocompleteOption) =>
  formatCustomerUserOptionLabel(option.name, option.customerCompanyName);

const CustomerUsersAutocomplete = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  sx,
}: Props) => {
  const { t } = useTranslation("common");
  const [inputValue, setInputValue] = useState("");
  const { customerUserOptions, isLoading, setSearchTerm, resetSearch } =
    useCustomerUsersAutocomplete();
  const selectedLabelRef = useRef(value ? getOptionLabel(value) : "");

  useEffect(() => {
    const label = value ? getOptionLabel(value) : "";
    selectedLabelRef.current = label;
    if (!value) {
      setInputValue("");
      resetSearch();
    }
  }, [value, resetSearch]);

  const handleInputChange = (
    _: SyntheticEvent,
    newValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "reset") {
      return;
    }

    setInputValue(newValue);

    if (newValue.trim()) {
      setSearchTerm(newValue);
    } else {
      resetSearch();
    }
  };

  const handleChange = (newValue: CustomerUserAutocompleteOption | null) => {
    const label = newValue ? getOptionLabel(newValue) : "";
    selectedLabelRef.current = label;
    setInputValue(label);
    resetSearch();
    onChange(newValue);
  };

  const handleClose = () => {
    setInputValue(selectedLabelRef.current);
    resetSearch();
  };

  const autocompleteOptions =
    value && !customerUserOptions.some((option) => option.id === value.id)
      ? [value, ...customerUserOptions]
      : customerUserOptions;

  return (
    <AutocompleteBaseSingle<CustomerUserAutocompleteOption>
      value={value}
      onChange={handleChange}
      options={autocompleteOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      filterOptions={(options) => options}
      loading={isLoading}
      loadingText={<LoadingSpinner />}
      disabled={disabled}
      placeholder={placeholder}
      noOptionsText={t("writeToSearch")}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onClose={handleClose}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText
            primary={getOptionLabel(option)}
            primaryTypographyProps={{ noWrap: true }}
          />
        </ListItem>
      )}
      sx={{
        "& .MuiAutocomplete-input": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        ...sx,
      }}
    />
  );
};

export default CustomerUsersAutocomplete;
